<template>
  <!-- Teleport na body: OrdersModal to Dialog z HeadlessUI, który przenosi swoją zawartość na koniec
       body. Renderowany w drzewie komponentu ten modal trafiał pod panel zamówień, bo jego z-index
       działał tylko wewnątrz zagnieżdżonych rodziców. -->
  <Teleport to="body">
  <div class="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-labelledby="fix-order-title">
    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="close()"></div>

    <div class="relative flex fix-order-panel w-full max-w-2xl flex-col overflow-hidden rounded-t-xl bg-white shadow-xl sm:rounded-xl">
      <!-- nagłówek -->
      <div class="flex items-start gap-4 border-b border-gray-200 px-6 py-5">
        <span class="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100">
          <CogIcon class="h-6 w-6 text-yellow-600" aria-hidden="true" />
        </span>
        <div class="min-w-0 flex-1">
          <h3 id="fix-order-title" class="text-base font-semibold text-gray-900">Napraw zamówienie</h3>
          <p class="mt-1 text-sm text-gray-500">
            Wskaż etykiety sztuk, które zostały fizycznie zabrane z magazynu. Dopiero wtedy towar zejdzie ze stanu.
          </p>
        </div>
        <button @click="close()" type="button" class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-gray-400 transition duration-200 hover:bg-gray-100 hover:text-gray-700">
          <span class="sr-only">Zamknij</span>
          <XIcon class="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <!-- treść -->
      <div class="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        <div v-if="loading" class="flex flex-col items-center justify-center gap-3 py-10 text-center">
          <RefreshIcon class="h-8 w-8 animate-spin text-indigo-500" aria-hidden="true" />
          <p class="text-sm text-gray-500">Pobieram rezerwacje z magazynu…</p>
        </div>

        <div v-else-if="loadError" class="flex flex-col items-center justify-center gap-3 py-10 text-center">
          <ExclamationIcon class="h-8 w-8 text-red-500" aria-hidden="true" />
          <p class="text-sm text-gray-700">{{ loadError }}</p>
          <button @click="loadReservations()" type="button" class="mt-1 inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
            Spróbuj ponownie
          </button>
        </div>

        <!-- brak rezerwacji: nie ma czego zdejmować -->
        <div v-else-if="slots.length === 0" class="flex flex-col items-center justify-center gap-3 py-10 text-center">
          <ExclamationIcon class="h-8 w-8 text-yellow-500" aria-hidden="true" />
          <p class="text-sm font-medium text-gray-900">To zamówienie nie ma rezerwacji w magazynie</p>
          <p class="max-w-sm text-sm text-gray-500">
            Nie ma czego zdejmować ze stanu. Możesz naprawić sam status zamówienia – stan magazynowy pozostanie bez zmian.
          </p>
        </div>

        <div v-else class="flex flex-col gap-4">
          <div v-for="(slot, index) in slots" :key="slot.key" class="rounded-lg border border-gray-200 p-4">
            <div class="flex flex-wrap items-baseline justify-between gap-2">
              <div class="min-w-0">
                <p class="truncate text-sm font-semibold text-gray-900">{{ slot.productName || slot.productId }}</p>
                <p class="mt-0.5 text-xs text-gray-500">
                  Paczka <span class="font-medium text-gray-700">{{ slot.partName }}</span>
                  · segment <span class="font-medium text-gray-700">{{ slot.segmentName }}</span>
                  <span v-if="slot.duplicateOf"> · sztuka {{ slot.copyIndex }} z {{ slot.copyTotal }}</span>
                </p>
              </div>
              <span v-if="slot.selected" class="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                <CheckIcon class="h-3.5 w-3.5" aria-hidden="true" />
                wybrano
              </span>
            </div>

            <label class="mt-3 block">
              <span class="text-xs font-medium uppercase tracking-wide text-gray-500">Zabrana etykieta</span>
              <select
                v-model="slot.selected"
                class="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              >
                <option :value="null">— wybierz etykietę —</option>
                <option
                  v-for="label in slot.labels"
                  :key="label"
                  :value="label"
                  :disabled="isLabelTakenElsewhere(label, index)"
                >
                  {{ label }}{{ isLabelTakenElsewhere(label, index) ? ' (już wybrana)' : '' }}
                </option>
              </select>
            </label>
            <p v-if="slot.labels.length === 0" class="mt-2 text-xs text-red-600">
              W tym segmencie nie ma żadnej etykiety – sprawdź stan fizycznie.
            </p>
          </div>
        </div>
      </div>

      <!-- stopka -->
      <div class="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row-reverse sm:items-center">
        <button
          @click="confirm()"
          :disabled="!canConfirm || saving"
          type="button"
          :class="[(!canConfirm || saving) ? 'cursor-not-allowed opacity-50' : 'hover:bg-indigo-500 active:scale-95', 'inline-flex fix-order-touch items-center justify-center gap-x-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200']"
        >
          <RefreshIcon v-if="saving" class="h-4 w-4 animate-spin" aria-hidden="true" />
          {{ saving ? 'Zapisuję…' : confirmLabel }}
        </button>
        <button @click="close()" :disabled="saving" type="button" class="inline-flex fix-order-touch items-center justify-center rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 transition-all duration-200 hover:bg-gray-50">
          Anuluj
        </button>
        <p v-if="slots.length > 0" class="flex-1 text-xs text-gray-500 sm:text-left">
          Wybrano {{ selectedCount }} z {{ slots.length }} {{ slots.length === 1 ? 'sztuki' : 'sztuk' }}.
        </p>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script>
import axios from 'axios';
import { CogIcon, XIcon, RefreshIcon, ExclamationIcon, CheckIcon } from '@heroicons/vue/solid';

export default {
  name: 'FixOrderModal',
  components: { CogIcon, XIcon, RefreshIcon, ExclamationIcon, CheckIcon },
  props: {
    order: { type: Object, required: true },
    route: { type: Object, default: null },
  },
  emits: ['close', 'done'],
  data() {
    return {
      loading: true,
      saving: false,
      loadError: '',
      slots: [],
    };
  },
  computed: {
    selectedCount() {
      return this.slots.filter(slot => slot.selected).length;
    },
    // Każda zarezerwowana sztuka musi mieć wskazaną etykietę – o to chodzi w tej zmianie.
    canConfirm() {
      if(this.slots.length === 0) return true;
      return this.slots.every(slot => !!slot.selected);
    },
    confirmLabel() {
      return this.slots.length === 0 ? 'Napraw sam status' : 'Zdejmij ze stanu i napraw';
    },
  },
  methods: {
    async loadReservations() {
      this.loading = true;
      this.loadError = '';
      try {
        const res = await axios.post(`${this.$store.state.apiLink}/order/reservations`, {
          orderId: this.order.id,
        });
        if(!res.data.success) throw res.data.log;

        // Jedna rezerwacja może obejmować kilka sztuk (np. dwie pozycje tego samego produktu),
        // więc rozbijamy ją na osobne wiersze – jeden wiersz = jedna etykieta do wskazania.
        const slots = [];
        for(const reservation of (res.data.reservations || [])) {
          const total = Math.max(1, Number(reservation.occurrences) || 1);
          for(let copy = 0; copy < total; copy++) {
            slots.push({
              key: `${reservation.warehouse.id}-${reservation.segment.id}-${reservation.partKey}-${copy}`,
              productId: reservation.productId,
              productName: reservation.productName,
              variantId: reservation.variantId,
              partKey: reservation.partKey,
              partName: reservation.partName,
              warehouseId: reservation.warehouse.id,
              segmentId: reservation.segment.id,
              segmentName: reservation.segment.name,
              labels: reservation.labels || [],
              packageId: this.matchPackageId(reservation, copy),
              duplicateOf: total > 1,
              copyIndex: copy + 1,
              copyTotal: total,
              selected: null,
            });
          }
        }
        this.slots = slots;
      } catch (error) {
        console.log(error);
        this.loadError = typeof error === 'string' ? error : 'Nie udało się pobrać rezerwacji.';
      } finally {
        this.loading = false;
      }
    },
    // Dopasowanie rezerwacji do konkretnej paczki zamówienia. Identyfikatory paczek mają w systemie
    // kilka formatów, więc szukamy po wariancie i kolejności, a nie po sklejaniu identyfikatora.
    matchPackageId(reservation, copyIndex) {
      const packages = Array.isArray(this.order.packages) ? this.order.packages : [];
      const candidates = packages.filter(parcel =>
        parcel && !parcel.subtracted && parcel.variantId === reservation.variantId);
      const alreadyUsed = this.slots.filter(slot => slot.variantId === reservation.variantId).length;
      const parcel = candidates[alreadyUsed + copyIndex] || candidates[0] || null;
      return parcel ? parcel.id : null;
    },
    isLabelTakenElsewhere(label, index) {
      return this.slots.some((slot, i) => i !== index && slot.selected === label);
    },
    async confirm() {
      if(!this.canConfirm || this.saving) return;
      this.saving = true;
      try {
        const selections = this.slots
          .filter(slot => slot.selected)
          .map(slot => ({
            packageId: slot.packageId,
            productId: slot.productId,
            variantId: slot.variantId,
            partKey: slot.partKey,
            partName: slot.partName,
            customPartId: slot.selected,
            warehouseId: slot.warehouseId,
            segmentId: slot.segmentId,
            segmentName: slot.segmentName,
          }));

        this.$emit('done', selections);
      } finally {
        this.saving = false;
      }
    },
    close() {
      if(this.saving) return;
      this.$emit('close');
    },
  },
  mounted() {
    // Panel zamówień jest na czas tego modala schowany, więc znika też blokada przewijania,
    // którą zakładał jego Dialog. Zakładamy własną – klasą, nie stylem inline, bo HeadlessUI
    // przy chowaniu panelu czyści `style.overflow` na <html> i skasowałby nam ją.
    document.documentElement.classList.add('fix-order-scroll-lock');
    this.loadReservations();
  },
  beforeUnmount() {
    document.documentElement.classList.remove('fix-order-scroll-lock');
  },
};
</script>

<style scoped>
/* Tailwind 2 (postcss7-compat, bez JIT) nie generuje klas z wartościami dowolnymi,
   np. max-h-[92vh] czy min-h-[44px] – takie klasy nie tworzą ŻADNEGO CSS.
   Konkretne wymiary trzymamy więc tutaj. */
.fix-order-panel {
  max-height: 92vh;
}
.fix-order-touch {
  min-height: 44px;
}
</style>

<!-- Niescopowany: celuje w <html>, więc nie może dostać atrybutu scope.
     `!important` bije ewentualny styl inline zostawiony przez HeadlessUI. -->
<style>
.fix-order-scroll-lock {
  overflow: hidden !important;
}
</style>
